/**
 * docExtras — the "extra text" a document carries beyond its numbers.
 *
 * Two kinds, and they are NOT the same thing:
 *   • line description — per item. Typed on /sales-new (detailed mode) or
 *     inherited from the product. On a Farm Service line the product name is
 *     literally "Farm Service:" and this is the ONLY record of what was sold.
 *   • document note    — one per document. On an expense it IS the expense
 *     ("load", "snack", "ca treaser") — 757 of 1,247 prod expenses carry one
 *     while only 111 carry a description.
 *
 * Both were stored all along and shown almost nowhere. This module is the one
 * place that decides what counts as an extra, so the detail window, the print
 * prompt and the print templates cannot drift apart (the "hand-copied routine"
 * bug family — see docs/POS-SESSION-HANDOFF-2026-09-22.md).
 *
 * Pure: no React, no DOM, no formatting.
 */

const clean = (v) => (v == null ? '' : String(v)).trim();

/**
 * The description worth showing for one line, or '' when there is none.
 * A description that merely repeats the product name is noise, not content.
 */
export function lineDescription(item) {
  const desc = clean(item && item.description);
  if (!desc) return '';
  if (desc.toLowerCase() === clean(item && item.product_name).toLowerCase()) return '';
  return desc;
}

/** The document's own note, or ''. Accepts the field names the families use. */
export function docNote(doc) {
  return clean(doc && doc.notes);
}

/**
 * Everything a print prompt needs to ask its question.
 *
 *   { descriptions: [{ name, text }], note, hasDescriptions, hasNote, hasAny }
 *
 * `descriptions` keeps the product name alongside the text so the prompt can
 * show the operator WHICH lines will gain a line of print — a checkbox that
 * says only "item descriptions" makes them guess.
 */
export function docExtras(doc) {
  const items = (doc && doc.items) || [];
  const descriptions = [];
  for (const item of items) {
    const text = lineDescription(item);
    if (text) descriptions.push({ name: clean(item.product_name) || 'Item', text });
  }
  const note = docNote(doc);
  return {
    descriptions,
    note,
    hasDescriptions: descriptions.length > 0,
    hasNote: note.length > 0,
    hasAny: descriptions.length > 0 || note.length > 0,
  };
}

/**
 * What the print templates should render, given the operator's answer.
 * Defaults to printing everything: a caller that has not asked (an expense
 * voucher, a reprint from a queue) must not silently lose the text.
 */
export function resolvePrintExtras(choice) {
  const c = choice || {};
  return {
    descriptions: c.descriptions !== false,
    note: c.note !== false,
  };
}

/**
 * Apply the operator's answer to a document ONCE, before it reaches a print
 * template. Whatever is switched off is simply not there any more, so every
 * template stays a plain renderer and no template can forget to check a flag.
 *
 * Returns the document unchanged when nothing is switched off, so the common
 * path allocates nothing.
 */
export function applyPrintExtras(doc, choice) {
  const want = resolvePrintExtras(choice);
  if (!doc || (want.descriptions && want.note)) return doc;
  const out = { ...doc };
  if (!want.note) out.notes = '';
  if (!want.descriptions && Array.isArray(out.items)) {
    out.items = out.items.map((it) => {
      // A line whose NAME is missing still needs the description to identify
      // itself — dropping it would print a nameless row.
      if (!clean(it && it.product_name)) return it;
      return { ...it, description: '' };
    });
  }
  return out;
}
